import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  inject,
  isDevMode,
} from '@angular/core';
import {
  provideRouter,
  Router,
  Scroll,
  TitleStrategy,
  UrlSerializer,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient, withXhr, withInterceptors } from '@angular/common/http';
import { ViewportScroller } from '@angular/common';
import { localizedErrorsInterceptor } from './core/localized-errors.interceptor';
import { filter } from 'rxjs';
import { provideTransloco } from '@jsverse/transloco';

import { routes } from './app.routes';
import { LangUrlSerializer } from './core/lang-url.serializer';
import { SeoService } from './core/services/seo.service';
import { TranslocoHttpLoader } from './core/services/transloco-loader';
import { SeoTitleStrategy } from './core/services/seo-title.strategy';

/**
 * Gestion fine du scroll de navigation :
 * - retour arrière / avant : restaure la position mémorisée ;
 * - ancre (#fragment) : gérée par le router (anchorScrolling) ;
 * - changement de page (le chemin change) : remonte en haut ;
 * - changement de query params uniquement (filtres type ?type=lxc) :
 *   NE bouge PAS le scroll.
 */
function setupScrollBehavior(): void {
  const router = inject(Router);
  const scroller = inject(ViewportScroller);
  let lastPath: string | null = null;
  router.events.pipe(filter((e): e is Scroll => e instanceof Scroll)).subscribe((e) => {
    const url = 'urlAfterRedirects' in e.routerEvent ? e.routerEvent.urlAfterRedirects : e.routerEvent.url;
    const path = url.split('?')[0].split('#')[0];
    if (e.position) {
      scroller.scrollToPosition(e.position);
    } else if (!e.anchor && lastPath !== null && path !== lastPath) {
      scroller.scrollToPosition([0, 0]);
    }
    lastPath = path;
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'disabled', anchorScrolling: 'enabled' }),
      withViewTransitions({ skipInitialTransition: true })
    ),
    { provide: TitleStrategy, useClass: SeoTitleStrategy },
    { provide: UrlSerializer, useClass: LangUrlSerializer },
    provideHttpClient(withXhr(), withInterceptors([localizedErrorsInterceptor])),
    provideAppInitializer(() => inject(SeoService).init()),
    provideAppInitializer(setupScrollBehavior),
    provideTransloco({
      config: {
        availableLangs: ['fr', 'en', 'es', 'it', 'de', 'pt', 'nl', 'pl', 'ro'],
        defaultLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
  ],
};
